// Visual theming helpers for achievements: per-event icon, per-tier color.

export interface TierTheme {
  label: string;
  emoji: string;
  /** Tailwind background gradient classes for the medal */
  medalBg: string;
  /** Tailwind text color for medal icon */
  medalText: string;
  /** Border color tailwind class for card */
  cardBorder: string;
  /** Soft glow color */
  glow: string;
}

export function getTierTheme(tier: number): TierTheme {
  switch (tier) {
    case 1:
      return {
        label: 'Bronce',
        emoji: '🥉',
        medalBg: 'from-orange-300 via-orange-500 to-amber-700',
        medalText: 'text-white',
        cardBorder: 'border-orange-500/30',
        glow: 'shadow-orange-500/20',
      };
    case 2:
      return {
        label: 'Plata',
        emoji: '🥈',
        medalBg: 'from-slate-200 via-slate-400 to-slate-600',
        medalText: 'text-white',
        cardBorder: 'border-slate-400/30',
        glow: 'shadow-slate-400/20',
      };
    case 3:
      return {
        label: 'Oro',
        emoji: '🥇',
        medalBg: 'from-yellow-300 via-yellow-500 to-amber-600',
        medalText: 'text-white',
        cardBorder: 'border-yellow-500/30',
        glow: 'shadow-yellow-500/30',
      };
    case 4:
      return {
        label: 'Platino',
        emoji: '💎',
        medalBg: 'from-cyan-300 via-cyan-400 to-blue-500',
        medalText: 'text-white',
        cardBorder: 'border-cyan-400/30',
        glow: 'shadow-cyan-400/30',
      };
    default:
      return {
        label: `Tier ${tier}`,
        emoji: '⭐',
        medalBg: 'from-purple-400 via-purple-600 to-purple-800',
        medalText: 'text-white',
        cardBorder: 'border-purple-500/30',
        glow: 'shadow-purple-500/30',
      };
  }
}

/**
 * Map an event name to a Material Symbols icon name.
 * Falls back to a generic emoji_events trophy when unknown.
 */
export function getEventIcon(eventType: string): string {
  const map: Record<string, string> = {
    'cv.uploaded': 'description',
    'cv.score.updated': 'grade',
    'linkedin.uploaded': 'badge',
    'linkedin.score.updated': 'workspace_premium',
    'interview.created': 'auto_awesome',
    'interview.uploaded': 'cloud_upload',
    'interview.hr.booked': 'event_available',
    'interview.hr.passed': 'handshake',
    'interview.technical.booked': 'code',
    'interview.design_system.passed': 'architecture',
    'algo.solved': 'bolt',
    'mentor.review.completed': 'reviews',
    'mentor.tier_check.completed': 'verified',
    'training.completed': 'school',
    'user.connected': 'login',
  };
  return map[eventType] ?? 'emoji_events';
}

export interface StreakTheme {
  label: string;
  icon: string;
  color: string;
  bg: string;
}

export function getStreakTheme(type: string): StreakTheme {
  const map: Record<string, StreakTheme> = {
    CONNECTION: { label: 'Conexión diaria', icon: 'whatshot', color: 'text-orange-400', bg: 'from-orange-500/20 to-red-500/10' },
    ACHIEVEMENT_COMPLETED: { label: 'Logros', icon: 'emoji_events', color: 'text-yellow-400', bg: 'from-yellow-500/20 to-amber-500/10' },
    ALGO_SOLVED: { label: 'Algoritmos', icon: 'bolt', color: 'text-cyan-400', bg: 'from-cyan-500/20 to-blue-500/10' },
    INTERVIEW_UPLOADED: { label: 'Entrevistas', icon: 'cloud_upload', color: 'text-purple-400', bg: 'from-purple-500/20 to-pink-500/10' },
  };
  return (
    map[type] ?? {
      label: type,
      icon: 'whatshot',
      color: 'text-primary',
      bg: 'from-primary/20 to-primary/10',
    }
  );
}

/**
 * Returns a friendly label like "3 / 7 días" or "score 87 / 90" for a given assignment progress + rule.
 */
export function formatProgressLabel(
  rule: { type: string; count?: number; value?: number; window?: string; field?: string },
  progress: { count?: number; thresholdReached?: boolean } | null | undefined,
  completed: boolean,
): string {
  if (completed) return '¡Completado!';
  const count = progress?.count ?? 0;
  switch (rule.type) {
    case 'event_first':
    case 'manual':
      return 'Hacelo una vez';
    case 'event_threshold':
      return progress?.thresholdReached
        ? '¡Umbral alcanzado!'
        : `Necesitas ${rule.field ?? 'valor'} ≥ ${rule.value ?? '?'}`;
    case 'event_count_total':
      return `${count} / ${rule.count ?? '?'}`;
    case 'event_count_window':
      return `${count} / ${rule.count ?? '?'} en ${rule.window ?? '7d'}`;
    default:
      return '';
  }
}
